<template>
  <lsm-widget
    :title="t('course.training_invitation')"
    :sub-title="courseSession?.course?.name"
    icon="fa-chalkboard-teacher"
    data-testid="course-session-invite-widget"
  >
    <v-card-text>
      {{ toLongDateWithDay(courseSession.date) }}<br>{{ courseSessionCaption }}
    </v-card-text>
    <v-card-text v-if="action">
      <v-icon
        size="large"
        icon="fas fa-check-circle"
        color="success"
      />
      {{ t('course.invitation_action_message', { action: action }) }}
    </v-card-text>
    <template
      v-if="!action"
      #buttons
    >
      <v-btn
        :to="`/training/${courseSession.id}`"
        :text="t('global.view_details')"
        color="primary"
      />
      <v-btn
        v-if="courseSession?.pivot?.status === 'pending' && !action"
        color="success"
        :text="t('actions.accept')"
        @click="acceptInvitation"
      />

      <v-btn
        color="error"
        @click="dialog = true"
      >
        {{ t('actions.decline') }}
        <v-dialog
          v-model="dialog"
          max-width="400px"
        >
          <global-card :title="t('course.decline_invitation')">
            {{ t('course.decline_invitation_confirmation') }}
            <template #actions>
              <v-btn
                :text="t('actions.close')"
                color="dark-grey"
                @click="close"
              />
              <v-btn
                v-if="!action"
                color="error"
                :text="t('course.decline_invitation')"
                @click="declineInvitation"
              />
            </template>
          </global-card>
        </v-dialog>
      </v-btn>
    </template>
  </lsm-widget>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDate } from '@/composables';
import { useErrorStore } from '@/stores/errors';
import LsmWidget from './LsmWidget.vue';

const props = defineProps<{
  courseSession: App.DataTables.NextCourseSession;
}>();

const emit = defineEmits(['reload']);

const { t } = useI18n();

const { toLongDateWithDay, toShortTime } = useDate();
const { handleError, showSnackMessage } = useErrorStore();

const action = ref<string>('');
const dialog = ref(false);

const courseSessionCaption = computed(() => {
  const begins = toShortTime(props.courseSession.begins);
  const ends = toShortTime(props.courseSession.ends);
  return `${begins} - ${ends}`;
});

function close() {
  dialog.value = false;
}

async function acceptInvitation() {
  const url = `/api/coursesessions/${props.courseSession.id}/accept`;
  try {
    await axios.put(url);
    action.value = t('states.accepted');
    setTimeout(() => {
      action.value = '';
      emit('reload');
    }, 3000);
  }
  catch (error) {
    handleError(error, 'Could not accept invitation');
  }
}
async function declineInvitation() {
  const url = `/api/coursesessions/${props.courseSession.id}/decline`;
  try {
    await axios.put(url);
    action.value = t('course.declined');
    showSnackMessage(t('course.decline_invitation_message'));
    emit('reload');
  }
  catch (error) {
    handleError(error, t('course.decline_invitation_error'));
  }
}
</script>
